import React from "react";
import { useTranslation } from "../i18n";
import { m } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Brain,
  Zap,
  ArrowRight,
  Layers,
  CheckCircle,
  Bot,
  Building2,
  FileText,
  Receipt,
  IdCard,
} from "lucide-react";
import { OTHER_SECTORS } from "../data/otherSectors";
import "./ScannersSection.css";

// Documentos de ejemplo y la plantilla a la que los envía la IA. Las plantillas
// son las mismas cuatro pestañas del widget del hero (`free.templates.*`).
const ROUTES = [
  { file: "IMG_2041.jpg", template: "invoice", icon: FileText },
  { file: "dni_frontal.jpg", template: "id", icon: IdCard },
  { file: "ticket_gasolinera.pdf", template: "receipt", icon: Receipt },
];

export default function ScannersSection() {
  const { t, lang } = useTranslation();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  return (
    <section
      id="scanners"
      className="py-24 relative overflow-hidden bg-gray-50/50"
    >
      <div className="scanners-bg absolute inset-0 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <m.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-6 leading-tight"
          >
            {t("scanners.hero_title") || "Intelligent Extraction Power"}
          </m.h2>
          <m.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-xl text-gray-600"
          >
            {t("scanners.hero_subtitle")}
          </m.p>
        </div>

        {/* Bento Grid */}
        <m.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6 auto-rows-[minmax(280px,auto)]"
        >
          {/* Card 1: AI Power (Large, spans 2 cols, 2 rows) */}
          <m.div
            variants={itemVariants}
            className="md:col-span-2 md:row-span-2 bento-card relative overflow-hidden group"
          >
            <div className="relative z-10 h-full flex flex-col justify-between p-8">
              <div>
                <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center text-primary-600 mb-6">
                  <Brain className="w-6 h-6" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  {t("scanners.feature_ai")}
                </h3>
                <p className="text-gray-600 text-lg">
                  {t("scanners.feature_ai_desc")}
                </p>
              </div>

              {/* Antes: una línea de escaneo sobre renglones abstractos. Se
                  sustituye porque «Cómo funciona», justo debajo, ya enseña el
                  escaneo con una factura real, y porque esta tarjeta habla de
                  otra cosa: de que la IA ELIGE la plantilla. Eso es lo que
                  dibuja ahora — cada documento llega a su plantilla. */}
              <div className="mt-8 rounded-xl border border-white/60 bg-white/60 p-5 shadow-sm space-y-3.5">
                {ROUTES.map((r, i) => (
                  <m.div
                    key={r.file}
                    initial={{ opacity: 0, x: -12 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.15 + i * 0.25, duration: 0.4 }}
                    className="flex items-center gap-2 sm:gap-3"
                  >
                    <span className="flex-1 min-w-0 inline-flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-2.5 text-sm text-gray-600">
                      <r.icon className="w-3.5 h-3.5 shrink-0 text-gray-400" />
                      <span className="truncate font-mono">{r.file}</span>
                    </span>
                    <ArrowRight className="w-4 h-4 shrink-0 text-primary-400" />
                    <span className="flex-1 min-w-0 inline-flex items-center gap-2 rounded-lg bg-primary-50 border border-primary-100 px-3 py-2.5 text-sm font-semibold text-primary-700">
                      <CheckCircle className="w-3.5 h-3.5 shrink-0" />
                      <span className="truncate">{t(`free.templates.${r.template}`)}</span>
                    </span>
                  </m.div>
                ))}
                <div className="pt-1 flex justify-end">
                  <span className="text-xs font-semibold text-gray-500">{t("scanners.accuracy")}</span>
                </div>
              </div>
            </div>
            {/* Hover Gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-green-50/0 to-green-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
          </m.div>

          {/* Card 2: Speed / Scale */}
          <m.div
            variants={itemVariants}
            className="md:col-span-1 bento-card p-8 group"
          >
            <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center text-amber-600 mb-6">
              <Zap className="w-6 h-6" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              {t("scanners.feature_scale")}
            </h3>
            <p className="text-gray-600 text-sm">
              {t("scanners.feature_scale_desc")}
            </p>
            <div className="mt-6 flex items-baseline gap-1">
              <span className="text-4xl font-extrabold text-gray-900 counter-animate">
                {t("scanners.speed_time")}
              </span>
              <span className="text-sm text-gray-500">
                {t("scanners.per_page")}
              </span>
            </div>
          </m.div>

          {/* Card 3: Sectores.

              Antes era un JSON de ejemplo con datos inventados («Empresa Acme»,
              fecha de 2024). La salida real, animada y con la confianza de cada
              campo, está ahora en «Cómo funciona», justo debajo: tenerla dos
              veces seguidas era el mismo problema que llevó a rehacer esa
              sección.

              En su lugar, enlaces a las páginas de sector. Además de orientar al
              visitante, son el primer enlace contextual desde la portada hacia
              `/sector/*`, que Google tenía como «rastreada, sin indexar» en
              parte por no recibir enlaces internos. */}
          <m.div
            variants={itemVariants}
            className="md:col-span-1 md:row-span-2 bento-card p-8 group flex flex-col"
          >
            <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center text-blue-600 mb-6">
              <Building2 className="w-6 h-6" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-5">
              {t("scanners.use_cases_title")}
            </h3>
            <ul className="space-y-2 flex-1">
              {Object.entries(OTHER_SECTORS).map(([key, names]) => (
                <li key={key}>
                  <Link
                    to={`/${lang}/sector/${key}`}
                    className="flex items-center justify-between gap-2 rounded-xl border border-gray-200 bg-white/70 px-3.5 py-2.5 text-sm font-medium text-gray-700 hover:border-primary-300 hover:text-primary-700 transition-colors"
                  >
                    <span>{names[lang] || names.en}</span>
                    <ArrowRight className="w-4 h-4 shrink-0 text-gray-300" />
                  </Link>
                </li>
              ))}
            </ul>
          </m.div>

          {/* Card 4: Integration */}
          <m.div
            variants={itemVariants}
            className="md:col-span-1 bento-card p-8 group"
          >
            <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center text-purple-600 mb-6">
              <Layers className="w-6 h-6" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              {t("scanners.steps.3.title")}
            </h3>
            <p className="text-gray-600 text-sm mb-4">
              {t("scanners.export_desc")}
            </p>

            <div className="flex gap-2 justify-start opacity-60 group-hover:opacity-100 transition-opacity">
              {/* Mock Integrations */}
              <div className="w-8 h-8 rounded bg-gray-200 flex items-center justify-center text-[10px] font-bold text-gray-500">
                SAP
              </div>
              <div className="w-8 h-8 rounded bg-blue-100 flex items-center justify-center text-[10px] font-bold text-blue-500">
                Xero
              </div>
              <div className="w-8 h-8 rounded bg-green-100 flex items-center justify-center text-[10px] font-bold text-green-600">
                XL
              </div>
            </div>
          </m.div>

          {/* Card 5: AI Assistant (New) */}
          <m.div
            variants={itemVariants}
            className="md:col-span-2 bento-card p-8 flex flex-row items-center justify-between gap-6 group cursor-pointer hover:border-primary-200"
          >
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center text-indigo-600">
                  <Bot className="w-5 h-5" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 leading-tight">
                  {t("scanners.ai_assistant_title") || "AI Schema Assistant"}
                </h3>
                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-indigo-100 text-indigo-600 uppercase tracking-wide">
                  {t("scanners.new_badge")}
                </span>
              </div>
              <p className="text-gray-600 text-sm max-w-md leading-relaxed">
                {t("scanners.ai_assistant_desc") ||
                  "Just describe your document in plain language. Our AI Assistant chats with you to define fields and instantly builds your custom scanner."}
              </p>
            </div>

            {/* Visual Chat Bubble Hint */}
            <div className="hidden sm:flex relative w-16 h-16 rounded-2xl bg-white border border-indigo-50 shadow-sm items-center justify-center group-hover:scale-110 group-hover:shadow-md transition-all duration-300">
              <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-indigo-600 border-2 border-white flex items-center justify-center">
                <Zap className="w-3 h-3 text-white" />
              </div>
              <div className="space-y-1.5 w-1/2">
                <div className="h-1.5 bg-gray-200 rounded-full w-full" />
                <div className="h-1.5 bg-gray-200 rounded-full w-2/3" />
              </div>
            </div>
          </m.div>
        </m.div>
      </div>
    </section>
  );
}
